// src/data/mock/team.ts

/**
 * Team Members Data Structure
 * 
 * Complete information about Champions Academy team members
 * including coaches, leadership, and support staff
 */

import { 
  Facebook, 
  Instagram, 
  Twitter, 
  Linkedin, 
  Mail, 
  Phone,
  Award,
  Trophy,
  Target,
  GraduationCap
} from 'lucide-react';

export type TeamCategory = 'leadership' | 'coach' | 'staff' | 'medical';
export type SportSpecialty = 'football' | 'basketball' | 'swimming' | 'karate' | 'tennis' | 'multi';

export interface TeamMember {
  id: string;
  name: {
    en: string;
    ar: string;
  };
  role: {
    en: string;
    ar: string;
  };
  category: TeamCategory;
  image: string;
  bio: {
    en: string;
    ar: string;
  };
  
  // Professional Details
  specialty?: SportSpecialty[];
  experience?: number; // years
  certifications?: {
    en: string[];
    ar: string[];
  };
  achievements?: {
    en: string[];
    ar: string[];
  };
  
  // Contact & Social
  email?: string;
  phone?: string;
  social?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
  };
  
  // Statistics (for coaches)
  stats?: {
    playersCoached?: number;
    yearsExperience?: number;
    championships?: number;
    successRate?: number;
  };
  
  // Additional Info
  languages?: string[];
  education?: {
    en: string;
    ar: string;
  };
  quote?: {
    en: string;
    ar: string;
  };
  
  featured?: boolean;
  active?: boolean;
}

/**
 * Team Members Database
 */
export const team: TeamMember[] = [
  // ===== LEADERSHIP =====
  {
    id: 'lead-001',
    name: {
      en: 'Ahmed Al-Masri',
      ar: 'أحمد المصري'
    },
    role: {
      en: 'Founder & CEO',
      ar: 'المؤسس والمدير التنفيذي'
    },
    category: 'leadership',
    image: '/assets/team/ahmed-almasri.jpg',
    bio: {
      en: 'Visionary leader with over 20 years of experience in sports development and youth empowerment. Founded Champions Academy with a mission to create opportunities for Palestinian youth through sports.',
      ar: 'قائد صاحب رؤية مع أكثر من 20 عاماً من الخبرة في تطوير الرياضة وتمكين الشباب. أسس أكاديمية تشامبيونز بهدف خلق فرص للشباب الفلسطيني من خلال الرياضة.'
    },
    experience: 20,
    education: {
      en: 'Master in Sports Management - Cairo University',
      ar: 'ماجستير في الإدارة الرياضية - جامعة القاهرة'
    },
    certifications: {
      en: [
        'UEFA A Coaching License',
        'Sports Management Professional',
        'Youth Development Specialist'
      ],
      ar: [
        'رخصة تدريب UEFA A',
        'محترف إدارة رياضية',
        'أخصائي تطوير الشباب'
      ]
    },
    achievements: {
      en: [
        'Founded 7 sports academies across Gaza',
        'Trained over 5,000 young athletes',
        'Winner of Palestinian Sports Excellence Award 2023'
      ],
      ar: [
        'تأسيس 7 أكاديميات رياضية في غزة',
        'تدريب أكثر من 5,000 رياضي شاب',
        'الفائز بجائزة التميز الرياضي الفلسطيني 2023'
      ]
    },
    email: 'ahmed@champions.ps',
    phone: '+970 59 123 4567',
    social: {
      linkedin: 'https://linkedin.com/in/ahmed-almasri',
      twitter: 'https://twitter.com/ahmed_almasri',
      facebook: 'https://facebook.com/ahmed.almasri'
    },
    languages: ['Arabic', 'English', 'French'],
    quote: {
      en: 'Every child deserves the chance to be a champion, regardless of their circumstances.',
      ar: 'كل طفل يستحق فرصة أن يكون بطلاً، بغض النظر عن ظروفه.'
    },
    featured: true,
    active: true
  },

  {
    id: 'lead-002',
    name: {
      en: 'Dr. Sarah Al-Hassan',
      ar: 'د. سارة الحسن'
    },
    role: {
      en: 'Director of Operations',
      ar: 'مديرة العمليات'
    },
    category: 'leadership',
    image: '/assets/team/sarah-alhassan.jpg',
    bio: {
      en: 'Experienced operations director with a PhD in Sports Science. Oversees all academy operations and ensures the highest standards of training and facility management.',
      ar: 'مديرة عمليات ذات خبرة مع دكتوراه في علوم الرياضة. تشرف على جميع عمليات الأكاديمية وتضمن أعلى معايير التدريب وإدارة المرافق.'
    },
    experience: 15,
    education: {
      en: 'PhD in Sports Science - University of Jordan',
      ar: 'دكتوراه في علوم الرياضة - الجامعة الأردنية'
    },
    email: 'sarah@champions.ps',
    phone: '+970 59 123 4568',
    social: {
      linkedin: 'https://linkedin.com/in/sarah-alhassan',
      instagram: 'https://instagram.com/dr.sarah.alhassan'
    },
    languages: ['Arabic', 'English'],
    featured: true,
    active: true
  },

  {
    id: 'lead-003',
    name: {
      en: 'Mahmoud Al-Najjar',
      ar: 'محمود النجار'
    },
    role: {
      en: 'Technical Director',
      ar: 'المدير الفني'
    },
    category: 'leadership',
    image: '/assets/team/mahmoud-alnajjar.jpg',
    bio: {
      en: 'Former professional athlete turned technical director. Designs training programs and oversees coaching quality across all sports disciplines.',
      ar: 'رياضي محترف سابق تحول إلى مدير فني. يصمم برامج التدريب ويشرف على جودة التدريب في جميع التخصصات الرياضية.'
    },
    experience: 18,
    specialty: ['multi'],
    email: 'mahmoud@champions.ps',
    active: true
  },

  // ===== FOOTBALL COACHES =====
  {
    id: 'coach-001',
    name: {
      en: 'Coach Tariq Hamdan',
      ar: 'المدرب طارق حمدان'
    },
    role: {
      en: 'Head Football Coach',
      ar: 'مدرب كرة القدم الرئيسي'
    },
    category: 'coach',
    specialty: ['football'],
    image: '/assets/team/tariq-hamdan.jpg',
    bio: {
      en: 'UEFA-certified football coach with 12 years of experience. Specializes in youth development and tactical training. Led teams to 5 regional championships.',
      ar: 'مدرب كرة قدم معتمد من UEFA مع 12 عاماً من الخبرة. متخصص في تطوير الشباب والتدريب التكتيكي. قاد الفرق إلى 5 بطولات إقليمية.'
    },
    experience: 12,
    certifications: {
      en: [
        'UEFA B Coaching License',
        'Youth Development Specialist',
        'Sports Psychology Certificate'
      ],
      ar: [
        'رخصة تدريب UEFA B',
        'أخصائي تطوير الشباب',
        'شهادة علم النفس الرياضي'
      ]
    },
    achievements: {
      en: [
        '5 Regional Championships',
        'Developed 15 professional players',
        'Coach of the Year 2024'
      ],
      ar: [
        '5 بطولات إقليمية',
        'طور 15 لاعباً محترفاً',
        'مدرب العام 2024'
      ]
    },
    stats: {
      playersCoached: 300,
      yearsExperience: 12,
      championships: 5,
      successRate: 85
    },
    email: 'tariq@champions.ps',
    phone: '+970 59 234 5678',
    social: {
      instagram: 'https://instagram.com/coach.tariq',
      facebook: 'https://facebook.com/tariq.hamdan'
    },
    languages: ['Arabic', 'English'],
    quote: {
      en: 'Football is not just a game, it\'s a way of life that teaches discipline and teamwork.',
      ar: 'كرة القدم ليست مجرد لعبة، إنها أسلوب حياة يعلم الانضباط والعمل الجماعي.'
    },
    featured: true,
    active: true
  },

  {
    id: 'coach-002',
    name: {
      en: 'Coach Yasser Abu Daqqa',
      ar: 'المدرب ياسر أبو دقة'
    },
    role: {
      en: 'Assistant Football Coach',
      ar: 'مساعد مدرب كرة القدم'
    },
    category: 'coach',
    specialty: ['football'],
    image: '/assets/team/yasser-abudaqqa.jpg',
    bio: {
      en: 'Experienced assistant coach focusing on technical skills and player fitness. Former professional player with extensive knowledge of the game.',
      ar: 'مدرب مساعد ذو خبرة يركز على المهارات الفنية ولياقة اللاعبين. لاعب محترف سابق مع معرفة واسعة باللعبة.'
    },
    experience: 8,
    specialty: ['football'],
    stats: {
      playersCoached: 200,
      yearsExperience: 8,
      championships: 3
    },
    email: 'yasser@champions.ps',
    active: true
  },

  // ===== BASKETBALL COACHES =====
  {
    id: 'coach-003',
    name: {
      en: 'Coach Nidal Qeshta',
      ar: 'المدرب نضال قشطة'
    },
    role: {
      en: 'Head Basketball Coach',
      ar: 'مدرب كرة السلة الرئيسي'
    },
    category: 'coach',
    specialty: ['basketball'],
    image: '/assets/team/nidal-qeshta.jpg',
    bio: {
      en: 'FIBA-certified basketball coach with international experience. Specializes in offensive strategies and player development programs.',
      ar: 'مدرب كرة سلة معتمد من FIBA مع خبرة دولية. متخصص في الاستراتيجيات الهجومية وبرامج تطوير اللاعبين.'
    },
    experience: 10,
    certifications: {
      en: [
        'FIBA Level 2 Coaching License',
        'Strength & Conditioning Specialist',
        'Sports Nutrition Certificate'
      ],
      ar: [
        'رخصة تدريب FIBA المستوى 2',
        'أخصائي قوة وتكييف',
        'شهادة التغذية الرياضية'
      ]
    },
    stats: {
      playersCoached: 180,
      yearsExperience: 10,
      championships: 4,
      successRate: 82
    },
    email: 'nidal@champions.ps',
    social: {
      instagram: 'https://instagram.com/coach.nidal'
    },
    languages: ['Arabic', 'English'],
    featured: true,
    active: true
  },

  // ===== SWIMMING COACHES =====
  {
    id: 'coach-004',
    name: {
      en: 'Coach Laila Al-Bayoumi',
      ar: 'المدربة ليلى البيومي'
    },
    role: {
      en: 'Head Swimming Coach',
      ar: 'مدربة السباحة الرئيسية'
    },
    category: 'coach',
    specialty: ['swimming'],
    image: '/assets/team/laila-albayoumi.jpg',
    bio: {
      en: 'Olympic-standard swimming coach with expertise in competitive swimming and water safety. Trained multiple national champions.',
      ar: 'مدربة سباحة بمعايير أولمبية مع خبرة في السباحة التنافسية والسلامة المائية. دربت عدة أبطال وطنيين.'
    },
    experience: 14,
    certifications: {
      en: [
        'FINA Swimming Coach Level 3',
        'Water Safety Instructor',
        'Competitive Swimming Specialist'
      ],
      ar: [
        'مدربة سباحة FINA المستوى 3',
        'مدربة سلامة مائية',
        'أخصائية سباحة تنافسية'
      ]
    },
    achievements: {
      en: [
        'Trained 8 national champions',
        '3 Olympic qualifiers',
        'Swimming Coach of the Year 2023'
      ],
      ar: [
        'دربت 8 أبطال وطنيين',
        '3 مؤهلين أولمبيين',
        'مدربة السباحة للعام 2023'
      ]
    },
    stats: {
      playersCoached: 250,
      yearsExperience: 14,
      championships: 6,
      successRate: 88
    },
    email: 'laila@champions.ps',
    phone: '+970 59 345 6789',
    social: {
      instagram: 'https://instagram.com/coach.laila',
      facebook: 'https://facebook.com/laila.albayoumi'
    },
    languages: ['Arabic', 'English', 'French'],
    quote: {
      en: 'In swimming, like in life, the only way forward is to keep moving.',
      ar: 'في السباحة، كما في الحياة، الطريقة الوحيدة للأمام هي الاستمرار في الحركة.'
    },
    featured: true,
    active: true
  },

  // ===== KARATE COACHES =====
  {
    id: 'coach-005',
    name: {
      en: 'Sensei Rami Al-Ghoul',
      ar: 'السنسي رامي الغول'
    },
    role: {
      en: 'Head Karate Instructor',
      ar: 'مدرب الكاراتيه الرئيسي'
    },
    category: 'coach',
    specialty: ['karate'],
    image: '/assets/team/rami-alghoul.jpg',
    bio: {
      en: '4th Dan Black Belt with 16 years of martial arts experience. Focuses on discipline, respect, and self-defense while building champions.',
      ar: 'حزام أسود من الدرجة الرابعة مع 16 عاماً من خبرة الفنون القتالية. يركز على الانضباط والاحترام والدفاع عن النفس مع بناء الأبطال.'
    },
    experience: 16,
    certifications: {
      en: [
        'WKF Certified Instructor',
        '4th Dan Black Belt',
        'Youth Martial Arts Specialist',
        'Sports Injury Prevention'
      ],
      ar: [
        'مدرب معتمد WKF',
        'حزام أسود الدرجة الرابعة',
        'أخصائي فنون قتالية للشباب',
        'الوقاية من الإصابات الرياضية'
      ]
    },
    achievements: {
      en: [
        '12 National Karate Championships',
        'Trained 20+ Black Belts',
        'International Kata Competition Winner'
      ],
      ar: [
        '12 بطولة كاراتيه وطنية',
        'درّب 20+ حزام أسود',
        'فائز بمسابقة كاتا الدولية'
      ]
    },
    stats: {
      playersCoached: 400,
      yearsExperience: 16,
      championships: 12,
      successRate: 90
    },
    email: 'rami@champions.ps',
    social: {
      instagram: 'https://instagram.com/sensei.rami',
      youtube: 'https://youtube.com/@senseirami'
    },
    languages: ['Arabic', 'English', 'Japanese'],
    quote: {
      en: 'Karate is not about winning or losing, it\'s about the journey of self-improvement.',
      ar: 'الكاراتيه ليست عن الفوز أو الخسارة، إنها عن رحلة تحسين الذات.'
    },
    featured: true,
    active: true
  },

  // ===== TENNIS COACHES =====
  {
    id: 'coach-006',
    name: {
      en: 'Coach Omar Al-Haj',
      ar: 'المدرب عمر الحاج'
    },
    role: {
      en: 'Head Tennis Coach',
      ar: 'مدرب التنس الرئيسي'
    },
    category: 'coach',
    specialty: ['tennis'],
    image: '/assets/team/omar-alhaj.jpg',
    bio: {
      en: 'ITF-certified tennis coach with experience training junior players. Emphasizes technique, mental strength, and competitive spirit.',
      ar: 'مدرب تنس معتمد من ITF مع خبرة في تدريب اللاعبين الناشئين. يؤكد على التقنية والقوة الذهنية والروح التنافسية.'
    },
    experience: 9,
    certifications: {
      en: [
        'ITF Level 2 Coach',
        'Tennis Performance Analyst',
        'Sports Psychology Certificate'
      ],
      ar: [
        'مدرب ITF المستوى 2',
        'محلل أداء التنس',
        'شهادة علم النفس الرياضي'
      ]
    },
    stats: {
      playersCoached: 120,
      yearsExperience: 9,
      championships: 3,
      successRate: 80
    },
    email: 'omar@champions.ps',
    active: true
  },

  // ===== MEDICAL & SUPPORT STAFF =====
  {
    id: 'staff-001',
    name: {
      en: 'Dr. Hana Abu Salem',
      ar: 'د. هناء أبو سالم'
    },
    role: {
      en: 'Sports Medicine Physician',
      ar: 'طبيبة طب رياضي'
    },
    category: 'medical',
    image: '/assets/team/hana-abusalem.jpg',
    bio: {
      en: 'Board-certified sports medicine physician ensuring athlete health and injury prevention. Provides medical support and rehabilitation services.',
      ar: 'طبيبة طب رياضي معتمدة تضمن صحة الرياضيين والوقاية من الإصابات. تقدم الدعم الطبي وخدمات التأهيل.'
    },
    experience: 11,
    education: {
      en: 'MD Sports Medicine - Alexandria University',
      ar: 'دكتوراه طب رياضي - جامعة الإسكندرية'
    },
    certifications: {
      en: [
        'Board Certified Sports Medicine',
        'Sports Injury Rehabilitation Specialist',
        'Nutrition & Performance Consultant'
      ],
      ar: [
        'طب رياضي معتمد',
        'أخصائية تأهيل إصابات رياضية',
        'استشارية تغذية وأداء'
      ]
    },
    email: 'hana@champions.ps',
    phone: '+970 59 456 7890',
    languages: ['Arabic', 'English'],
    active: true
  },

  {
    id: 'staff-002',
    name: {
      en: 'Physiotherapist Khaled Masoud',
      ar: 'أخصائي العلاج الطبيعي خالد مسعود'
    },
    role: {
      en: 'Head Physiotherapist',
      ar: 'أخصائي العلاج الطبيعي الرئيسي'
    },
    category: 'medical',
    image: '/assets/team/khaled-masoud.jpg',
    bio: {
      en: 'Licensed physiotherapist specializing in sports injuries and rehabilitation. Works closely with coaches to optimize athlete performance.',
      ar: 'أخصائي علاج طبيعي مرخص متخصص في الإصابات الرياضية والتأهيل. يعمل بشكل وثيق مع المدربين لتحسين أداء الرياضيين.'
    },
    experience: 8,
    email: 'khaled@champions.ps',
    active: true
  },

  {
    id: 'staff-003',
    name: {
      en: 'Nour Al-Din',
      ar: 'نور الدين'
    },
    role: {
      en: 'Facilities Manager',
      ar: 'مدير المرافق'
    },
    category: 'staff',
    image: '/assets/team/nour-aldin.jpg',
    bio: {
      en: 'Experienced facilities manager ensuring all sports facilities are maintained to the highest standards for optimal training conditions.',
      ar: 'مدير مرافق ذو خبرة يضمن صيانة جميع المرافق الرياضية بأعلى المعايير لظروف تدريب مثالية.'
    },
    experience: 12,
    email: 'nour@champions.ps',
    active: true
  },

  {
    id: 'staff-004',
    name: {
      en: 'Rana Al-Masri',
      ar: 'رنا المصري'
    },
    role: {
      en: 'Admissions Coordinator',
      ar: 'منسقة القبول'
    },
    category: 'staff',
    image: '/assets/team/rana-almasri.jpg',
    bio: {
      en: 'Friendly and professional admissions coordinator helping families navigate enrollment and program selection.',
      ar: 'منسقة قبول ودودة ومحترفة تساعد العائلات في التسجيل واختيار البرنامج.'
    },
    experience: 5,
    email: 'rana@champions.ps',
    phone: '+970 59 567 8901',
    active: true
  },

  {
    id: 'staff-005',
    name: {
      en: 'Media Team - Bilal Issa',
      ar: 'فريق الإعلام - بلال عيسى'
    },
    role: {
      en: 'Media & Communications Manager',
      ar: 'مدير الإعلام والاتصالات'
    },
    category: 'staff',
    image: '/assets/team/bilal-issa.jpg',
    bio: {
      en: 'Creative media professional managing academy communications, social media, and public relations.',
      ar: 'محترف إعلام مبدع يدير اتصالات الأكاديمية ووسائل التواصل الاجتماعي والعلاقات العامة.'
    },
    experience: 7,
    email: 'media@champions.ps',
    social: {
      instagram: 'https://instagram.com/bilal.issa',
      twitter: 'https://twitter.com/bilal_issa'
    },
    active: true
  }
];

/**
 * Team Statistics
 */
export const teamStats = {
  totalMembers: team.length,
  coaches: team.filter(m => m.category === 'coach').length,
  leadership: team.filter(m => m.category === 'leadership').length,
  staff: team.filter(m => m.category === 'staff').length,
  medical: team.filter(m => m.category === 'medical').length,
  totalExperience: team.reduce((sum, m) => sum + (m.experience || 0), 0),
  averageExperience: Math.round(
    team.reduce((sum, m) => sum + (m.experience || 0), 0) / team.filter(m => m.experience).length
  ),
  certifications: team.reduce(
    (sum, m) => sum + (m.certifications?.en.length || 0), 0
  ),
  languages: [...new Set(team.flatMap(m => m.languages || []))].length
};

/**
 * Helper Functions
 */
export const getTeamByCategory = (category: TeamCategory) => {
  return team.filter(member => member.category === category);
};

export const getFeaturedTeam = () => {
  return team.filter(member => member.featured === true);
};

export const getTeamBySpecialty = (specialty: SportSpecialty) => {
  return team.filter(member => 
    member.specialty?.includes(specialty)
  );
};

export const getActiveTeam = () => {
  return team.filter(member => member.active !== false);
};

// Export default
export default {
  team,
  teamStats,
  getTeamByCategory,
  getFeaturedTeam,
  getTeamBySpecialty,
  getActiveTeam
};
